/**
 * InvoiceRelatedDialog — zeigt welches Abo eine Rechnung gekauft hat
 * und welche Termine das Abo verbraucht haben.
 *
 * Iter 06/2026 (User-Wunsch Markus): „Im Kundenprofil bei jeder Rechnung
 * sehen, welche Pakete gekauft wurden und auf welche Termine sie
 * verwendet wurden."
 *
 * Backend: GET /api/invoices/{id}/related
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Receipt, Package, Calendar, User, CheckCircle2, XCircle, AlertCircle, ExternalLink, CreditCard, Trash2, Undo2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return iso;
  }
};
const fmtCHF = (n) => `CHF ${Number(n || 0).toFixed(2)}`;

const StatusBadge = ({ status }) => {
  const s = (status || 'unknown').toLowerCase();
  if (['completed', 'checked_in', 'used', 'scheduled', 'confirmed'].includes(s)) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">
        <CheckCircle2 className="h-3 w-3 mr-1" /> verbraucht
      </Badge>
    );
  }
  if (s === 'cancelled' || s === 'canceled') {
    return (
      <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 border-rose-200">
        <XCircle className="h-3 w-3 mr-1" /> storniert
      </Badge>
    );
  }
  if (s === 'no_show' || s === 'noshow') {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
        <AlertCircle className="h-3 w-3 mr-1" /> no-show
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs">
      {s}
    </Badge>
  );
};

export const InvoiceRelatedDialog = ({ open, onOpenChange, invoiceId, onChanged }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // 'unpay' | 'refund' | null — inline Bestätigung vor destruktiver Aktion
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!open || !invoiceId) {
      setData(null);
      setError(null);
      setConfirmAction(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/invoices/${encodeURIComponent(invoiceId)}/related`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch((err) => {
        if (active) {
          console.error('related fetch err', err);
          setError(err?.response?.data?.detail || 'Fehler beim Laden');
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, invoiceId, refreshKey]);

  const runPaymentAction = async (action) => {
    // action: 'unpay' (Zahlung löschen) | 'refund' (als Guthaben erstatten)
    //         | 'undo-refund' (Erstattung zurückziehen)
    const realId = data?.invoice?.id || invoiceId;
    const endpoint =
      action === 'unpay' ? 'unpay' : action === 'undo-refund' ? 'undo-refund' : 'refund-payment';
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/invoices/${encodeURIComponent(realId)}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Erfolgreich');
      setConfirmAction(null);
      setRefreshKey((k) => k + 1);
      onChanged?.();
    } catch (err) {
      console.error('payment action err', err);
      toast.error(err?.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="invoice-related-dialog"
        aria-describedby="invoice-related-desc"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={20} />
            Rechnungs-Details
          </DialogTitle>
        </DialogHeader>
        <p id="invoice-related-desc" className="sr-only">
          Details zur Rechnung: gekauftes Paket und damit verbrauchte Termine.
        </p>

        {loading ? (
          <div className="text-center py-8 text-sm text-zinc-500">Laden…</div>
        ) : error ? (
          <div className="text-center py-8 text-sm text-rose-600">{error}</div>
        ) : data ? (
          <div className="space-y-5">
            {/* Rechnungs-Header */}
            <div className="p-4 bg-zinc-50 rounded-lg border" data-testid="invoice-related-header">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Rechnung</p>
                  <p className="font-semibold text-zinc-900 text-lg">
                    {data.invoice.invoice_number}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {fmtDate(data.invoice.issue_date)}
                    {data.invoice.due_date ? ` · Fällig ${fmtDate(data.invoice.due_date)}` : ''}
                    {data.invoice.paid_at ? ` · Bezahlt ${fmtDate(data.invoice.paid_at)}` : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-zinc-900">
                    {fmtCHF(data.invoice.total_amount)}
                  </p>
                  <Badge
                    className={
                      data.invoice.status === 'paid'
                        ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200'
                        : data.invoice.status === 'refunded'
                        ? 'bg-zinc-100 text-zinc-700 hover:bg-zinc-100 border-zinc-300'
                        : 'border-amber-300 text-amber-700 bg-amber-50'
                    }
                  >
                    {data.invoice.status === 'paid'
                      ? 'bezahlt'
                      : data.invoice.status === 'refunded'
                      ? 'erstattet'
                      : data.invoice.status === 'pending'
                      ? 'offen'
                      : data.invoice.status}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Gekauftes Paket */}
            <div data-testid="invoice-related-subscription">
              <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                <Package size={16} />
                Gekauftes Paket
              </h4>
              {data.subscription ? (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="font-medium text-emerald-900">{data.subscription.name}</p>
                  <div className="mt-2 flex gap-4 text-xs">
                    <span className="text-emerald-700">
                      Gesamt: <span className="font-bold">{data.subscription.credits_total}</span>
                    </span>
                    <span className="text-emerald-700">
                      Verbraucht: <span className="font-bold">{data.subscription.credits_used}</span>
                    </span>
                    <span className="text-emerald-700">
                      Verbleibend: <span className="font-bold">{data.subscription.credits_remaining}</span>
                    </span>
                  </div>
                  <div className="w-full bg-emerald-200 rounded-full h-1.5 mt-2">
                    <div
                      className="bg-emerald-600 h-1.5 rounded-full transition-all"
                      style={{
                        width: `${
                          data.subscription.credits_total > 0
                            ? (data.subscription.credits_remaining / data.subscription.credits_total) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-zinc-500 italic">
                  Diese Rechnung ist keinem Paket zugeordnet.
                </p>
              )}
            </div>

            {/* Termine */}
            <div data-testid="invoice-related-appointments">
              <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                <Calendar size={16} />
                Verbrauchte Termine ({data.appointments?.length || 0})
              </h4>
              {data.appointments && data.appointments.length > 0 ? (
                <div className="space-y-2">
                  {data.appointments.map((apt) => (
                    <div
                      key={apt.appointment_id}
                      className="p-3 bg-zinc-50 rounded-lg border"
                      data-testid={`invoice-related-apt-${apt.appointment_id}`}
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-zinc-900 truncate">
                            {apt.service_name || 'Unbekannt'}
                          </p>
                          <p className="text-xs text-zinc-500 mt-0.5">
                            {(() => {
                              const st = apt.start_time || '';
                              const d = apt.date || st.slice(0, 10);
                              const hm = st.includes('T') ? st.slice(11, 16) : st;
                              return `${fmtDate(d)}${hm ? ` · ${hm} Uhr` : ''}`;
                            })()}
                          </p>
                          {apt.trainer_name && (
                            <p className="text-xs text-zinc-600 mt-1 flex items-center gap-1">
                              <User className="h-3 w-3" /> {apt.trainer_name}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <StatusBadge status={apt.status} />
                          {apt.credits_used ? (
                            <span className="text-[10px] text-zinc-500">
                              −{apt.credits_used} Credit{apt.credits_used !== 1 ? 's' : ''}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm py-4 text-center italic">
                  {data.subscription
                    ? 'Noch keine Termine mit diesem Paket gebucht.'
                    : 'Keine Termine verknüpft.'}
                </p>
              )}
            </div>

            {/* Zahlungen (User-Wunsch 06/2026: „Wie komme ich zur Zahlung?") */}
            {data.payments && data.payments.length > 0 && (
              <div data-testid="invoice-related-payments">
                <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <CreditCard size={16} />
                  Zahlungen ({data.payments.length})
                </h4>
                <div className="space-y-2">
                  {data.payments.map((pay) => (
                    <div
                      key={pay.id}
                      className="p-3 bg-zinc-50 rounded-lg border flex justify-between items-center gap-3"
                      data-testid={`invoice-payment-${pay.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-900 text-sm truncate">
                          {pay.description || 'Zahlung'}
                        </p>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {fmtDate(pay.created_at)}
                          {pay.payment_method ? ` · ${pay.payment_method}` : ''}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold text-zinc-900 text-sm">{fmtCHF(pay.amount)}</p>
                        <Badge
                          className={
                            pay.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200 text-[10px]'
                              : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-100 border-zinc-200 text-[10px]'
                          }
                        >
                          {pay.status === 'completed' ? 'abgeschlossen' : pay.status === 'refunded' ? 'erstattet' : pay.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Zahlungs-Aktionen für bezahlte/erstattete Rechnungen */}
            {(data.invoice.status === 'paid' || data.invoice.status === 'refunded') && (
              <div
                className="p-3 bg-rose-50/60 border border-rose-200 rounded-lg"
                data-testid="invoice-payment-actions"
              >
                {confirmAction ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-rose-900">
                      {confirmAction === 'unpay'
                        ? 'Zahlung wirklich löschen? Die Rechnung wird wieder als „offen" markiert und ggf. gutgeschriebene Punkte werden zurückgebucht.'
                        : confirmAction === 'undo-refund'
                        ? `Erstattung wirklich zurückziehen? CHF ${Number(data.invoice.total_amount || 0).toFixed(2)} werden vom Kundenguthaben abgezogen, die Rechnung wird wieder als „bezahlt" markiert und Punkte/Abo werden wiederhergestellt.`
                        : `Zahlung wirklich erstatten? CHF ${Number(data.invoice.total_amount || 0).toFixed(2)} werden dem Kundenguthaben gutgeschrieben und ggf. Punkte zurückgebucht.`}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        disabled={actionLoading}
                        onClick={() => runPaymentAction(confirmAction)}
                        data-testid="confirm-payment-action-btn"
                      >
                        {actionLoading ? 'Wird ausgeführt…' : 'Ja, bestätigen'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actionLoading}
                        onClick={() => setConfirmAction(null)}
                        data-testid="cancel-payment-action-btn"
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                ) : data.invoice.status === 'paid' ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs text-rose-800 w-full mb-1 font-medium">
                      Zahlung korrigieren:
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-100"
                      onClick={() => setConfirmAction('unpay')}
                      data-testid="unpay-invoice-btn"
                    >
                      <Trash2 size={14} className="mr-1.5" />
                      Zahlung löschen (Rechnung wieder offen)
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={() => setConfirmAction('refund')}
                      data-testid="refund-invoice-btn"
                    >
                      <Undo2 size={14} className="mr-1.5" />
                      Als Guthaben erstatten
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs text-rose-800 w-full mb-1 font-medium">
                      Erstattung korrigieren:
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      onClick={() => setConfirmAction('undo-refund')}
                      data-testid="undo-refund-invoice-btn"
                    >
                      <Undo2 size={14} className="mr-1.5" />
                      Erstattung zurückziehen (wieder bezahlt)
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-center py-8 text-zinc-500">Keine Daten verfügbar</p>
        )}

        <div className="flex justify-between items-center mt-4">
          {/* Direkt zur Rechnung springen (Finanzen → Rechnungen, vorgefiltert)
              — User-Wunsch 06/2026 */}
          {data?.invoice?.invoice_number ? (
            <Button
              onClick={() => {
                onOpenChange(false);
                navigate(`/invoices?search=${encodeURIComponent(data.invoice.invoice_number)}`);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="invoice-related-goto-invoice"
            >
              <ExternalLink size={14} className="mr-1.5" />
              Zur Rechnung
            </Button>
          ) : <span />}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="invoice-related-close"
          >
            Schliessen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceRelatedDialog;
